import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { formatDateTimeAmORPm } from "../../utils/common";
import UserDetailsModal from "../../components/UserDetails";
import { withSearchIcon } from "../../utils/helper";

interface User {
  id: number;
  created_at?: string;
  updated_at?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  gender?: string;
  dob?: string;
  profile_photo?: string;
  occupation?: string;
  height?: string;
  country?: string;
  city?: string;
  last_seen?: string;
  active?: boolean;
  visible?: boolean;
  last_visited?: string;
  blocked?: boolean;
  blocker_id?: number;
  blocker_type?: string;
  profile_status?: string;
  is_logged_in?: boolean;
  login_source?: string;
  meeting_purpose?: string;
  interested_in_gender?: string;
  [key: string]: any;
}
export default function EventBasedGuestList() {
  const [guests, setGuests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // ✅ Get eventId from query params
  const navigate = useNavigate();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId || "");
  const [viewModal, setViewModal] = useState<{
    open: boolean;
    user?: User;
  }>({ open: false });
  const [activeUserTab, setActiveUserTab] = useState("Personal Info");
  const [customLoader, setCustomLoader] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const columns: any = [
    { key: "userId", label: "ID" },
    {
      key: "profilePhoto",
      label: "Profile Photo",
      render: (val: any) =>
        val ? (
          <div className="flex items-center gap-2">
            <img
              src={val}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border"
            />
            <span
              onClick={() => {
                navigator.clipboard.writeText(val);
                toast.success("Profile URL copied!");
              }}
              className="text-xs text-blue-500 hover:underline cursor-pointer"
            >
              Copy URL
            </span>
          </div>
        ) : (
          "N/A"
        ),
    },
    { key: "firstName", label: withSearchIcon("First Name") },
    { key: "lastName", label: withSearchIcon("Last Name") },
    { key: "joiningStatus", label: "RSVP Status" },

    { key: "email", label: "Email" },
    { key: "phoneNo", label: "Phone No" },
    {
      key: "gender",
      label: "Gender",
      render: (val: any) => {
        const badgeClass =
          val === "Men"
            ? "bg-blue-100 text-blue-800"
            : val === "Women"
              ? "bg-pink-100 text-pink-800"
              : "bg-gray-100 text-gray-800";
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}
          >
            {val || "N/A"}
          </span>
        );
      },
    },
    {
      key: "eventId",
      label: "Event Id",
      render: (val: any) => {
        return val || "";
      },
    },
    {
      key: "eventName",
      label: "Event Name",
      render: (val: any) => {
        return val || "";
      },
    },
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded space-x-2 bg-white/30 backdrop-blur-md shadow-md",
      render: (_: any, row: any) => (
        <button
          onClick={() => handleViewUser(row.userId)}
          className="px-3 py-1 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          View
        </button>
      ),
    },
  ];

  useEffect(() => {
    fetchGuests(eventId, page, search);
  }, [eventId, page, search]);

  const fetchGuests = async (
    eventId?: number,
    activePageNo?: number,
    searchTerm?: any,
  ) => {
    try {
      setLoading(true);

      // Build params conditionally
      const params: any = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
        ...(eventId ? { eventId } : {}), // ✅ adds eventId only if it exists
      };

      const response: any = await apiService.get(
        "/event/users/get-event-guest-list",
        params,
      );

      setGuests(response?.data?.data || []);
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Failed to fetch guests");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchKey?: string) => {
    console.log("Searching for Event ID:", searchKey);
    const params = new URLSearchParams(location.search);

    if (searchKey && searchKey.trim() !== "") {
      // ✅ Add/replace eventId
      params.set("eventId", searchKey.trim());
    } else {
      // ❌ searchKey empty → remove eventId
      params.delete("eventId");
    }

    // 🚀 Update URL (no reload)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    // ✅ Update local state if needed
    setEventId(searchKey || "");
    setPage(1);
  };

  const handleExportGuestListCSV = async () => {
    try {
      setExportingCSV(true);

      const res: any = await apiService.get(
        "/export/export-guest-list",
        { eventId: eventId || "" },
        { responseType: "blob" },
      );

      if (res) {
        const blob =
          res instanceof Blob
            ? res
            : new Blob([res], { type: "text/csv;charset=utf-8;" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `guest_list_event_${eventId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Guest list CSV exported successfully.");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Failed to export guest list CSV. Please try again.");
    } finally {
      setExportingCSV(false);
    }
  };

  const handleViewUser = async (userId: number) => {
    try {
      setCustomLoader(true);
      setViewModal({ open: true });
      const res: any = await apiService.get(`/users/get-users`, { userId });

      if (res?.data?.users && res?.data?.users[0]) {
        setViewModal({ open: true, user: res?.data?.users[0] });
      } else {
        toast.error("User data not found.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch user details");
    } finally {
      setCustomLoader(false);
    }
  };
  return (
    <Layout
      pageTitle="Guest List"
      eventId={eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <>
        <div className="min-h-screen bg-gray-50 py-2 px-4">
          <DynamicTable
            title={`Guest List`}
            data={guests}
            columns={columns}
            pageSize={pageSize}
            loading={loading}
            activeNUmber={page}
            onPageChange={(newPage) => setPage(newPage)}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onSearch={(val?: any) => {
              setPage(1);
              setSearch(val);
            }}
            exportTypes={["csv"]}
            handleExportEventsCSV={handleExportGuestListCSV}
            exportLoading={exportingCSV}
            exportCsvTitle="Export Guest List CSV"
          />
        </div>
        {/* VIEW USER DETAILS MODAL */}
        <UserDetailsModal
          open={viewModal.open}
          user={viewModal.user}
          onClose={() => setViewModal({ open: false })}
          loading={customLoader}
          title="Guest Details"
        />
      </>
    </Layout>
  );
}
